import moment from "moment";
import React, { useState, useEffect } from "react";
import { AVAILABILITY_TYPES, DND_ITEM_TYPES, SHIFT_TYPES } from "~/src/consts";
import { POOL_TYPES } from "@teamrota/rota-common";
import {
  GridCardEmptyWrapper,
  PlusIcon,
  StyledShiftCell
} from "../../../GridCard/grid-card.styles";
import { Role } from "@teamrota/authlib";

import useAuth from "~/src/auth/hooks/use-auth";

import AvailableMemberBadge from "../../../AvailableMemberBadge";
import UnavailableGridCard from "../../../UnavailableGridCard/unavailable-grid-card";
import GridCard from "../../../GridCard/grid-card";
import { useDrop } from "react-dnd";
import MemberAddWarningModal from "~/src/components/StaffZonesPanel/components/MemberAddWarningModal";
import { toDateRange } from "~/src/utils/date-range";
import { useMemberDetails } from "./graphql";

const ShiftCell = ({
  cellIndex,
  dayIndex,
  scheduledMember,
  startTime,
  isDailyView,
  isWeeklyView,
  isMemberAvailabilityEnabled,
  onPressGridCell,
  onPressShiftCell,
  onPressAvailabilityBadge,
  showCancelledShifts,
  assignMemberToShift
}) => {
  const [isCellLoading, setIsCellLoading] = useState(false);
  const [lastDroppedShift, setLastDroppedShift] = useState(null);
  const [dragWarning, setDragWarning] = useState(null);

  const { memberData, memberDataLoading } = useMemberDetails({
    lastDroppedShift,
    id: scheduledMember?.member.id,
    dates: toDateRange(lastDroppedShift ?? {})
  });

  const assignMember = async () => {
    await assignMemberToShift({
      memberId: scheduledMember.member.id,
      shiftId: lastDroppedShift.id
    });

    setLastDroppedShift(null);
    setIsCellLoading(false);
  };

  useEffect(() => {
    if (memberData && !memberDataLoading) {
      if (
        memberData.isHitWorkingHoursLimit ||
        memberData.isConflicted ||
        memberData.isLocationConflict ||
        memberData.isUnavailable ||
        !memberData.isQualifiedWithRole ||
        !memberData.isQualifiedWithTags ||
        !memberData.isInVenueServiceArea ||
        memberData.relationship?.poolType === POOL_TYPES.BLACKLISTED
      ) {
        setDragWarning({
          member: memberData,
          callback: assignMember
        });
      } else {
        assignMember();
      }
    }
  }, [memberDataLoading]);

  const isDayShiftCell = cellIndex === 0;
  const isNightShiftCell = cellIndex === 1;

  const [{ draggedShift, isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: DND_ITEM_TYPES.OPEN_SHIFT,
      drop: shift => {
        setIsCellLoading(true);
        setLastDroppedShift(shift);
      },
      canDrop: shift =>
        ((shift.type === SHIFT_TYPES.DAY || shift.type === null) &&
          isDayShiftCell) ||
        (shift.type === SHIFT_TYPES.NIGHT && isNightShiftCell),
      collect: monitor => ({
        isOver: monitor.isOver(),
        draggedShift: monitor.getItem(),
        canDrop: monitor.canDrop()
      })
    }),
    []
  );

  const canDropShiftOnCell = (draggedShift && canDrop) || !draggedShift;

  const getInitialShiftData = rowDayIndex => {
    const weekStartTime = moment(startTime)?.add(rowDayIndex, "d");
    onPressGridCell({
      startTime: weekStartTime,
      scheduledMember
    });
  };

  const auth = useAuth();

  const isCurrentDayBooking = !!scheduledMember?.scheduledDays?.[dayIndex]
    ?.bookings?.length;

  const isCurrentDayInFuture =
    moment() < moment(startTime)?.add(dayIndex + 1, "d");

  const memberBookings = scheduledMember?.scheduledDays?.[
    dayIndex
  ]?.bookings.filter(booking => booking.state !== "APPLIED");

  const availability =
    isMemberAvailabilityEnabled &&
    scheduledMember?.scheduledDays?.[dayIndex]?.availability;

  const unavailability =
    scheduledMember?.scheduledDays?.[dayIndex]?.unavailability;

  const isNightShift = memberBookings?.[0]?.shift?.type === SHIFT_TYPES.NIGHT;

  const isShowShiftInColumn =
    isWeeklyView ||
    (isDayShiftCell && !isNightShift) ||
    (isNightShiftCell && isNightShift);

  const isAvailabilityNightShift =
    availability?.shiftType === AVAILABILITY_TYPES.NIGHT;

  const isAvailabilityAllDayShift =
    availability?.shiftType === AVAILABILITY_TYPES.ALL;

  const isSameShiftType =
    (!isNightShift && !isAvailabilityNightShift) ||
    (isNightShift && isAvailabilityNightShift);

  const isAvailabilityShiftCell =
    (isDayShiftCell && !isAvailabilityNightShift) ||
    (isNightShiftCell && isAvailabilityNightShift);

  const isShowAvailabilityInColumn =
    isWeeklyView || isAvailabilityAllDayShift || isAvailabilityShiftCell;

  const shouldShowCreateButton =
    auth.hasRole(Role.SHIFTS_CREATE) &&
    !isCurrentDayBooking &&
    !unavailability &&
    isCurrentDayInFuture;

  return (
    <StyledShiftCell
      key={cellIndex}
      isGreyCell={!isCurrentDayInFuture || !canDropShiftOnCell}
      highlightCell={isOver && canDropShiftOnCell}
      isCellLoading={isCellLoading}
      isNightShiftCell={isNightShiftCell}
    >
      {isCurrentDayBooking && isShowShiftInColumn && (
        <GridCard
          key={cellIndex}
          booking={
            showCancelledShifts
              ? memberBookings
              : memberBookings.filter(booking => !booking?.shift?.cancelledAt)
          }
          id={scheduledMember?.id + cellIndex}
          days={scheduledMember?.scheduledDays}
          onClick={onPressShiftCell}
          openCreateShift={() => getInitialShiftData(dayIndex)}
        />
      )}
      {!isCurrentDayBooking && unavailability && (
        <UnavailableGridCard
          unavailability={unavailability}
          onClick={() => getInitialShiftData(dayIndex)}
        />
      )}
      {availability && isShowAvailabilityInColumn && (
        <AvailableMemberBadge
          availability={availability}
          isDailyView={isDailyView}
          isWeeklyView={isWeeklyView}
          isNightShift={isNightShift && isNightShiftCell}
          isCurrentDayBooking={isCurrentDayBooking}
          isSameShiftType={isSameShiftType || isAvailabilityAllDayShift}
          onClick={onPressAvailabilityBadge}
        />
      )}
      {shouldShowCreateButton && (
        <GridCardEmptyWrapper
          ref={drop}
          lineHeight="12px"
          onClick={() => getInitialShiftData(dayIndex)}
        >
          <PlusIcon name="PLUS" size="small" color="#C4C4C4" />
        </GridCardEmptyWrapper>
      )}

      <MemberAddWarningModal
        accountType={"provider"}
        roleRateId={lastDroppedShift?.roleRate?.id}
        dates={[toDateRange(lastDroppedShift ?? {})]}
        warning={dragWarning}
        onClose={isAddingMember => {
          !isAddingMember && setIsCellLoading(false);
          setDragWarning(null);
          setLastDroppedShift(null);
        }}
      />
    </StyledShiftCell>
  );
};

export default ShiftCell;
